import React from 'react';
import PropTypes from 'prop-types';
import { themeColorKeys } from 'styles/utils';

import { IconStyled, PathStyled } from './Icon.styled';

export const iconSize = {
  XSMALL: 'xsmall',
  SMALL: 'small',
  DEFAULT: 'default',
  MEDIUM: 'medium',
  XMEDIUM: 'xmedium',
  LARGE: 'large',
  XLARGE: 'xlarge',
  XXLARGE: 'xxlarge',
  XXXLARGE: 'xxxlarge',
};

export const iconStroke = {
  LIGHT: 'light',
  REGULAR: 'regular',
  BOLD: 'bold',
};

function Icon({
  viewBox = '0 0 24 24',
  className = null,
  name,
  title = null,
  description = null,
  block = false,
  colorPath = 'currentColor',
  customStroke = iconStroke.LIGHT,
  onClick = undefined,
  ...props
}) {
  return (
    <IconStyled
      xmlns='http://www.w3.org/2000/svg'
      viewBox={viewBox}
      className={className}
      aria-labelledby={title ? 'title' : undefined}
      aria-describedby={description ? 'desc' : undefined}
      block={block}
      aria-hidden={!title}
      role='img'
      onClick={onClick}
      {...props}
    >
      {title && <title id='title'>{title}</title>}
      {description && <desc id='description'>{description}</desc>}
      <PathStyled
        pathName={name}
        colorPath={colorPath}
        customStroke={customStroke}
        fillRule='evenodd'
      />
    </IconStyled>
  );
}

Icon.defaultProps = {
  viewBox: '0 0 24 24',
  size: '1em',
  customStroke: iconStroke.LIGHT,
};

Icon.propTypes = {
  /**
   * Defines the position and dimension, in user space, of an SVG viewport.
   */
  viewBox: PropTypes.string,
  /**
   * Proportional size for width and height values
   */
  size: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(Object.values(iconSize)),
  ]),
  /**
   * Allows showing an <a href="https://storybook.wikifactory.com/?path=/docs/4--page">icon
   * present on the theme</a> by name.
   */
  name: PropTypes.string.isRequired,
  /**
   * Provides an accessible, short-text description of the SVG container
   */
  title: PropTypes.string,
  /**
   * Provides an accessible, long-text description of the SVG container
   */
  description: PropTypes.string,
  className: PropTypes.string,
  /**
   * Can display the Icon as a inline or block element
   */
  block: PropTypes.bool,
  /**
   * String color value or a theme color key
   */
  colorPath: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(themeColorKeys),
  ]),
  /**
   * Define the thickness of the icon's stroke
   */
  customStroke: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onClick: PropTypes.func,
  /**
   * The same as margin CSS property using a list of predefined sizes (theme.spacing). For example 'default 0' will be the same as '1rem 0'. The theme.spacing keys will be replaced by the correspondent value.
   */
  margin: PropTypes.string,
};

export default Icon;
